/**
 * Where the widget lives, from its own `<script src>`: `…/test/bugs/widget.js` → API `…/test/bugs/api`, MCP
 * `…/test/bugs/mcp`, host base path `/test`. A module script (dev page) has no `currentScript`; it names its base with
 * `data-bugs-base`.
 */
export interface Bases {
  /** Absolute: `https://host/test/bugs`. */
  bugs: string;
  /** Absolute: `https://host/test/bugs/api`. */
  api: string;
  /** Host app base path: `''` or `/test`. */
  host: string;
}

export function deriveBases(src: string | null | undefined, here: string): Bases {
  let url: URL;
  try {
    url = new URL(src || '/bugs/widget.js', here);
  } catch {
    url = new URL('/bugs/widget.js', here);
  }
  const path = url.pathname.replace(/\/+$/, '');
  // A base given directly (`/bugs`) has no file name; a script path ends with one.
  const bugsPath = /\.[a-z0-9]+$/i.test(path) ? path.replace(/\/[^/]*$/, '') : path;
  const bugs = `${url.origin}${bugsPath}`;
  const host = /\/bugs$/.test(bugsPath) ? bugsPath.slice(0, -'/bugs'.length) : '';
  return { bugs, api: `${bugs}/api`, host };
}

function scriptSource(): string | null {
  if (typeof document === 'undefined') return null;
  const current = document.currentScript as HTMLScriptElement | null;
  if (current?.hasAttribute('data-bugs-base')) return current.getAttribute('data-bugs-base');
  if (current?.src) return current.src;
  const marked = document.querySelector<HTMLScriptElement>('script[data-bugs-base]');
  if (marked) return marked.getAttribute('data-bugs-base');
  const found = document.querySelector<HTMLScriptElement>('script[src*="/bugs/widget.js"]');
  return found?.src ?? null;
}

// Read at module evaluation: `document.currentScript` is only set while the script runs.
let bases: Bases = deriveBases(scriptSource(), typeof window === 'undefined' ? 'http://localhost/' : window.location.href);

export const getBases = (): Bases => bases;

/** For tests. */
export function setBases(next: Bases): void {
  bases = next;
}
